import { asText } from '@prismicio/helpers';
import { hasRichText } from '@publicss/utils';
import { gsap } from 'gsap';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import {
  PageBodyBlock_Photo_LeftFields,
  PageBodyBlock_Photo_LeftPrimary,
} from '../../../types/prismic';
import useFadeUp from '../../hooks/useFadeUp';
import { media } from '../../styles/media';
import { Button } from '../ui';

interface BlockPhotoLeftProps {
  slice: {
    primary: PageBodyBlock_Photo_LeftPrimary;
    items: PageBodyBlock_Photo_LeftFields[];
  };
  sliceIndex: number;
}

const BlockPhotoLeft: React.FunctionComponent<BlockPhotoLeftProps> = ({
  slice: {
    primary: {
      heading,
      image_title,
      eyebrow,
      image,
      button_1_text,
      button_1_url,
      slice_anchor,
    },
    items,
  },
  sliceIndex,
}) => {
  const sectionId = slice_anchor?.length
    ? slice_anchor
    : `block-photo-left-${sliceIndex}`;

  useFadeUp(`#${sectionId} .fade-up`);

  useEffect(() => {
    const bgFadeIn = document.querySelectorAll(`#${sectionId} .bg--swipe`);

    bgFadeIn.forEach(function (element) {
      const bgTL = gsap.timeline({
        scrollTrigger: {
          trigger: element,
          start: 'top center',
          scrub: 1,
          onEnter: () =>
            element
              .querySelector('.bg--swipe--wrapper')
              ?.classList.add('is-active'),
        },
      });

      bgTL.fromTo(
        element.querySelector('.bg--swipe--img'),
        { scale: 1.1 },
        { scale: 1, force3D: true },
        0
      );
    });
  }, []);

  return (
    <Section id={sectionId} className="bg--main mt-5 md:mt-6 lg:mt-10">
      <div className="container relative z-10">
        <div className="grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12">
          {hasRichText(eyebrow) && (
            <div className="col-span-4 mb-12 md:col-span-8 md:mb-16 lg:col-span-12 lg:mb-5">
              <div className="eyebrow fade-up text-grey-5">
                {' '}
                / {asText(eyebrow.richText)}
              </div>
            </div>
          )}
          <div className="col-span-4 lg:col-span-5">
            <h2 className="h2 color--primary fade-up mb-12">
              {heading.richText[0].text}
            </h2>
            {image_title && (
              <p className="p small color--black mb-8 md:mb-14">
                {image_title}
              </p>
            )}
            {image.url && (
              <div className="bg--swipe relative">
                <div className="bg--swipe--wrapper">
                  <div className="bg--swipe--img">
                    <img src={image?.url} alt={image?.alt} className="block" />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="col-span-4 col-start-1 mt-12 pb-5 sm:ms-10 md:col-span-3 md:col-start-6 md:ms-0 md:mt-0 md:pb-24 lg:col-span-4 lg:col-start-9 lg:pb-10">
            {items?.map((item, index) => (
              <div key={index}>
                <h3
                  className={
                    'h4 color--black fade-up heading--num mb-4 md:mb-8'
                  }
                  data-num={item.number}>
                  {asText(item.heading.richText)}
                </h3>
                <p className="small fade-up mb-8 text-grey-5">
                  {asText(item.content.richText)}
                </p>
                {index !== 3 ? <hr className={'hr--color--black mb-8'} /> : ''}
              </div>
            ))}
            {button_1_text && button_1_url && (
              <Button
                href={button_1_url}
                className={
                  'btn btn--accent-1 color--black my-4 inline-block !border-none sm-down:w-full'
                }>
                {button_1_text}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
};

export default BlockPhotoLeft;

const Section = styled.section`
  overflow: hidden;
  .bg--swipe--wrapper {
    position: relative;
    width: 150%;
    inset-inline-end: 0;
    height: 185px;
    @media ${media.mdUp} {
      position: absolute;
      height: 400%;
    }
    @media ${media.lgUp} {
      height: 145%;
    }
  }
  .bg--swipe {
    padding-top: 0;
    @media ${media.mdUp} {
      padding-top: 116%;
    }
  }
  @media ${media.lgUp} {
    .heading--num::before {
      content: attr(data-num);
      position: absolute;
      inset-inline-start: -120px;
      text-align: end;
    }
  }
`;
